import { DbTransaction } from "@tc/middy-decorators"
import { templateRevision } from "@tc/drizzle-v2"

export const isTemplateOutdated = async (
  tx: DbTransaction,
  currentTemplateVersionId: string,
): Promise<boolean> => {
  const result = await isTemplateOutdatedBatch(tx, [currentTemplateVersionId])
  return result.get(currentTemplateVersionId) ?? false
}

/**
/**
 * Batch check template outdated status. Eagerly loads ALL template revision versions on first call,
 * then all lookups are pure in-memory comparisons. Cached across chunks within same Lambda invocation.
 */
let outdatedDataLoaded = false
const allTemplateVersions = new Map<string, { templateId: string; majorVersion: number; minorVersion: number }>()
const latestApprovedByTemplateId = new Map<string, { majorVersion: number; minorVersion: number }>()

const ensureOutdatedDataLoaded = async (tx: DbTransaction) => {
  if (outdatedDataLoaded) return

  const allRevisions = await tx
    .select({
      id: templateRevision.id,
      templateId: templateRevision.templateId,
      majorVersion: templateRevision.majorVersion,
      minorVersion: templateRevision.minorVersion,
      status: templateRevision.status,
    })
    .from(templateRevision)

  for (const row of allRevisions) {
    allTemplateVersions.set(row.id, {
      templateId: row.templateId,
      majorVersion: row.majorVersion,
      minorVersion: row.minorVersion,
    })

    if (row.status === "APPROVED") {
      const existing = latestApprovedByTemplateId.get(row.templateId)
      if (
        !existing ||
        row.majorVersion > existing.majorVersion ||
        (row.majorVersion === existing.majorVersion && row.minorVersion > existing.minorVersion)
      ) {
        latestApprovedByTemplateId.set(row.templateId, {
          majorVersion: row.majorVersion,
          minorVersion: row.minorVersion,
        })
      }
    }
  }
  outdatedDataLoaded = true
}

export const isTemplateOutdatedBatch = async (
  tx: DbTransaction,
  templateVersionIds: readonly string[],
): Promise<Map<string, boolean>> => {
  if (templateVersionIds.length === 0) return new Map()

  await ensureOutdatedDataLoaded(tx)

  const result = new Map<string, boolean>()
  for (const id of templateVersionIds) {
    const current = allTemplateVersions.get(id)
    if (!current) {
      result.set(id, false)
      continue
    }
    const latest = latestApprovedByTemplateId.get(current.templateId)
    if (!latest) {
      result.set(id, false)
      continue
    }
    result.set(
      id,
      current.majorVersion < latest.majorVersion ||
        (current.majorVersion === latest.majorVersion &&
          current.minorVersion < latest.minorVersion),
    )
  }
  return result
}
